import type { AuthUser, LoginResponse, Role } from '@/core/auth/types';
import { apiClient } from '@/core/api-client/client';

export async function login(
  email: string,
  password: string,
  mfaCode?: string,
): Promise<LoginResponse> {
  const { data } = await apiClient.post<LoginResponse>('/auth/login', {
    email,
    password,
    mfaCode,
  });
  return data;
}

export async function getMe(): Promise<AuthUser> {
  const { data } = await apiClient.get<AuthUser>('/auth/me');
  return data;
}

export async function updateMe(body: {
  fullName?: string;
  email?: string;
}): Promise<AuthUser> {
  const { data } = await apiClient.patch<AuthUser>('/auth/me', body);
  return data;
}

export async function changePassword(body: {
  currentPassword: string;
  newPassword: string;
}): Promise<{ message: string; emailed: boolean }> {
  const { data } = await apiClient.post<{ message: string; emailed: boolean }>(
    '/auth/me/change-password',
    body,
  );
  return data;
}

export async function uploadAvatar(file: File): Promise<AuthUser> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await apiClient.post<AuthUser>('/auth/me/avatar', form, {
    // Let the browser set multipart boundary (instance default is application/json).
    headers: { 'Content-Type': undefined },
  });
  return data;
}

export async function deleteAvatar(): Promise<AuthUser> {
  const { data } = await apiClient.delete<AuthUser>('/auth/me/avatar');
  return data;
}

export async function refresh(refreshToken: string): Promise<{ accessToken: string; refreshToken?: string }> {
  const { data } = await apiClient.post('/auth/refresh', { refreshToken });
  return data;
}

export async function verifyMfa(code: string): Promise<LoginResponse> {
  const { data } = await apiClient.post<LoginResponse>('/auth/mfa/verify', { code });
  return data;
}

export async function forgotPassword(
  email: string,
): Promise<{ message: string; emailed: boolean; reset_link?: string | null }> {
  const { data } = await apiClient.post<{
    message: string;
    emailed: boolean;
    reset_link?: string | null;
  }>('/auth/forgot-password', { email });
  return data;
}

export async function resetPassword(
  token: string,
  newPassword: string,
): Promise<{ message: string; emailed: boolean; reset_link?: string | null }> {
  const { data } = await apiClient.post<{
    message: string;
    emailed: boolean;
    reset_link?: string | null;
  }>('/auth/reset-password', { token, newPassword });
  return data;
}

/** Shared password for `scripts/seed_all.py` demo accounts. */
export const DEMO_PASSWORD = 'Password123!';

/**
 * Seeded demo personas from the local API (`seed_all.py`).
 * Used on the login page for one-click real sign-in in development.
 */
export const DEV_LOGIN_USERS: Array<{
  label: string;
  email: string;
  password: string;
  role: Role;
}> = [
  {
    label: 'School Admin',
    email: 'admin@demo.rcats.local',
    password: DEMO_PASSWORD,
    role: 'school_admin',
  },
  {
    label: 'Teacher',
    email: 'teacher@demo.rcats.local',
    password: DEMO_PASSWORD,
    role: 'teacher',
  },
  {
    label: 'Counselor',
    email: 'counselor@demo.rcats.local',
    password: DEMO_PASSWORD,
    role: 'counselor',
  },
  {
    label: 'Parent',
    email: 'parent@demo.rcats.local',
    password: DEMO_PASSWORD,
    role: 'parent',
  },
  {
    label: 'Student',
    email: 'student@demo.rcats.local',
    password: DEMO_PASSWORD,
    role: 'student',
  },
  {
    label: 'University',
    email: 'university@demo.rcats.local',
    password: DEMO_PASSWORD,
    role: 'university',
  },
  {
    label: 'Employer',
    email: 'employer@demo.rcats.local',
    password: DEMO_PASSWORD,
    role: 'employer',
  },
  {
    label: 'Super Admin',
    email: 'super@demo.rcats.local',
    password: DEMO_PASSWORD,
    role: 'super_admin',
  },
];
